import React, { type FC, useState } from 'react'

import { NotificationSeverity, selectIsUsersManagementRegistryLocked, selectLoadedRegistryUnlockedUntil, Translation } from '../../../../domain'

import { InlineAlert } from '../../common'
import { useTranslator } from '../../Internationalization'
import { useIntervalEffect } from '../../Interval'
import { useAppState } from '../../State'

const COUNTDOWN_TICK_MS = 1_000

/**
 * Migrated from a persistent (`persist: true`) notistack snackbar to an `InlineAlert`
 * (lore/engineering/CC-4102-notifications/Notifications_Migration.plan.md Step 8) - Toast has no
 * "never auto-dismiss" variant, and this message's whole point is to stay visible for as long as
 * self-registration is open, however long that is. No usecase involved: `InlineAlert` is
 * stateless/generic, this feature calls it directly off state it already reads, matching Step 2's
 * "push business limits up to the caller" design.
 */
export const RegistrationMessage: FC = () => {
    const locked = useAppState(selectIsUsersManagementRegistryLocked)
    const unlockedUntil = useAppState(selectLoadedRegistryUnlockedUntil)
    const translator = useTranslator()

    /**
     * The original rendered a self-updating `<FormattedRelativeTime>` (ticks every second).
     * `InlineAlert`'s content is a plain string, so this re-renders on the same cadence instead,
     * recomputing the formatted string fresh each time.
     */
    const [, forceTick] = useState(0)
    useIntervalEffect(() => forceTick((tick) => tick + 1), COUNTDOWN_TICK_MS)

    /** Hidden once the time is up, before the registry is relocked */
    if (unlockedUntil === null || locked || unlockedUntil.getTime() <= Date.now()) {
        return null
    }

    return (
        <InlineAlert
            severity={NotificationSeverity.INFO}
            content={{
                kind: 'descriptionOnly',
                description: translator.formatTranslation(Translation.CLIENT_REGISTRY_SELF_REGISTRATION, {
                    formattedTimeLeft: translator.formatTimeLeft(unlockedUntil),
                }),
            }}
        />
    )
}
